import React, { useEffect, useState, useRef } from 'react';
import 'aframe';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES } from '../constants';
import { fetchWithRetry } from '../utils/tourUtils';
import { toast } from '../components/ui/use-toast';

const LidarViewer = () => {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [measureMode, setMeasureMode] = useState(true);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const selectedPointsRef = useRef([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [csvLoaded, setCsvLoaded] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    point1: null,
    point2: null,
    distance: null,
    measurementMode: false,
  });
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    image: null,
    csv: null,
  });

  useEffect(() => {
    // refreshSessions();
  }, []);

  useEffect(() => {
    selectedPointsRef.current = selectedPoints;
  }, [selectedPoints]);

  const refreshSessions = async () => {
    try {
      const data = await fetchWithRetry({
        url: `${API_BASE_URL}/api/lidar/sessions`,
        label: 'fetch LiDAR sessions',
      });
      setSessions(data);
      if (data.length > 0) {
        setSelectedSession(data[0]);
        loadSession(data[0]);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const calculateDistance = (a, b) => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return (Math.sqrt(dx * dx + dy * dy + dz * dz) * 100).toFixed(1);
  };

  const clearMeasurement = () => {
    console.log('clearMeasurement called');
    const line = document.getElementById('measureLine');
    line.setAttribute('visible', 'false');
    document.getElementById('measureLabel').setAttribute('text', 'value:');
    setSelectedPoints([]);
    selectedPointsRef.current = [];
    setDebugInfo(prev => ({
      ...prev,
      point1: null,
      point2: null,
      distance: null,
      measurementMode: measureMode,
    }));
    document.querySelectorAll('.clickable').forEach(p => {
      p.setAttribute('color', '#00ffff');
    });
  };

  const handleImageUpload = event => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onload = e => {
        const sky = document.getElementById('sky');
        sky.setAttribute('src', e.target.result);
        setImageLoaded(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCSVUpload = event => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFiles(prev => ({ ...prev, csv: file }));
      setLoading(true);
      const reader = new FileReader();
      reader.onload = e => {
        const lines = e.target.result.split('\n').slice(1);
        const cloud = document.getElementById('pointCloud');
        cloud.innerHTML = '';

        lines.forEach(line => {
          const [x, y, z] = line.trim().split(',').map(Number);
          if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
            const point = document.createElement('a-sphere');
            point.setAttribute('radius', '0.05');
            point.setAttribute('color', '#00ffff');
            point.setAttribute('position', `${x} ${z} ${y}`);
            point.setAttribute('class', 'clickable');

            point.addEventListener('click', () => {
              if (!measureMode) return;

              console.log('Current selectedPoints:', selectedPointsRef.current);

              document.querySelectorAll('.clickable').forEach(p => {
                p.setAttribute('color', '#00ffff');
              });

              point.setAttribute('color', '#ffcc00');
              const pos = point.getAttribute('position');
              const vector = { x: pos.x, y: pos.y, z: pos.z };

              if (selectedPointsRef.current.length === 0) {
                console.log('Setting first point:', vector);
                setSelectedPoints([vector]);
                setDebugInfo(prev => ({
                  ...prev,
                  point1: {
                    position: vector,
                    originalCoords: { x, y, z },
                  },
                  point2: null,
                  distance: null,
                }));

                toast({
                  title: 'First point selected',
                  description: `Position: X: ${x.toFixed(2)}, Y: ${y.toFixed(2)}, Z: ${z.toFixed(2)}`,
                  duration: 3000,
                });
              } else if (selectedPointsRef.current.length === 1) {
                console.log('Setting second point:', vector);
                const point1 = selectedPointsRef.current[0];
                const point2 = vector;
                const dist = calculateDistance(point1, point2);

                const line = document.getElementById('measureLine');
                line.setAttribute(
                  'line',
                  `start: ${point1.x} ${point1.y} ${point1.z}; end: ${point2.x} ${point2.y} ${point2.z}; color: #ff0000`
                );
                line.setAttribute('visible', 'true');

                const midX = (point1.x + point2.x) / 2;
                const midY = (point1.y + point2.y) / 2;
                const midZ = (point1.z + point2.z) / 2;
                document
                  .getElementById('measureLabel')
                  .setAttribute('position', `${midX} ${midY + 0.2} ${midZ}`);
                document.getElementById('measureLabel').setAttribute('text', `value: ${dist} cm`);

                setMeasurements(prev => [...prev, { distance: dist, points: [point1, point2] }]);

                setDebugInfo(prev => ({
                  ...prev,
                  point2: {
                    position: vector,
                    originalCoords: { x, y, z },
                  },
                  distance: dist,
                }));

                toast({
                  title: 'Measurement Complete',
                  description: `Distance: ${dist} cm`,
                  duration: 4000,
                });

                setTimeout(() => {
                  console.log('Clearing measurement');
                  clearMeasurement();
                }, 4000);
              }
            });

            cloud.appendChild(point);
          }
        });
        setLoading(false);
        setCsvLoaded(true);
      };
      reader.readAsText(file);
    }
  };

  const loadSession = async sessionName => {
    try {
      setLoading(true);
      setImageLoaded(false);
      clearMeasurement();

      const { csv, image } = await fetchWithRetry({
        url: `${API_BASE_URL}/api/lidar/sessions/${sessionName}`,
        label: 'fetch LiDAR session data',
      });

      const sky = document.getElementById('sky');
      sky.setAttribute('src', image);
      setImageLoaded(true);

      const lines = csv.split('\n').slice(1);
      const cloud = document.getElementById('pointCloud');
      cloud.innerHTML = '';

      lines.forEach(line => {
        const [x, y, z] = line.trim().split(',').map(Number);
        if (!isNaN(x) && !isNaN(y) && !isNaN(z)) {
          const point = document.createElement('a-sphere');
          point.setAttribute('radius', '0.05');
          point.setAttribute('color', '#00ffff');
          point.setAttribute('position', `${x} ${z} ${y}`);
          point.setAttribute('class', 'clickable');

          point.addEventListener('click', () => {
            if (!measureMode) return;

            console.log('Current selectedPoints:', selectedPointsRef.current);

            document.querySelectorAll('.clickable').forEach(p => {
              p.setAttribute('color', '#00ffff');
            });

            point.setAttribute('color', '#ffcc00');
            const pos = point.getAttribute('position');
            const vector = { x: pos.x, y: pos.y, z: pos.z };

            if (selectedPointsRef.current.length === 0) {
              console.log('Setting first point:', vector);
              setSelectedPoints([vector]);
              setDebugInfo(prev => ({
                ...prev,
                point1: {
                  position: vector,
                  originalCoords: { x, y, z },
                },
                point2: null,
                distance: null,
              }));

              toast({
                title: 'First point selected',
                description: `Position: X: ${x.toFixed(2)}, Y: ${y.toFixed(2)}, Z: ${z.toFixed(2)}`,
                duration: 3000,
              });
            } else if (selectedPointsRef.current.length === 1) {
              console.log('Setting second point:', vector);
              const point1 = selectedPointsRef.current[0];
              const point2 = vector;
              const dist = calculateDistance(point1, point2);

              const line = document.getElementById('measureLine');
              line.setAttribute(
                'line',
                `start: ${point1.x} ${point1.y} ${point1.z}; end: ${point2.x} ${point2.y} ${point2.z}; color: #ff0000`
              );
              line.setAttribute('visible', 'true');

              const midX = (point1.x + point2.x) / 2;
              const midY = (point1.y + point2.y) / 2;
              const midZ = (point1.z + point2.z) / 2;
              document
                .getElementById('measureLabel')
                .setAttribute('position', `${midX} ${midY + 0.2} ${midZ}`);
              document.getElementById('measureLabel').setAttribute('text', `value: ${dist} cm`);

              setMeasurements(prev => [...prev, { distance: dist, points: [point1, point2] }]);

              setDebugInfo(prev => ({
                ...prev,
                point2: {
                  position: vector,
                  originalCoords: { x, y, z },
                },
                distance: dist,
              }));

              toast({
                title: 'Measurement Complete',
                description: `Distance: ${dist} cm`,
                duration: 4000,
              });

              setTimeout(() => {
                console.log('Clearing measurement');
                clearMeasurement();
              }, 4000);
            }
          });

          cloud.appendChild(point);
        }
      });
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setLoading(false);
    }
  };

  const startNewSession = async () => {
    try {
      setLoading(true);
      await fetchWithRetry({
        url: `${API_BASE_URL}/api/lidar/sessions/start`,
        method: 'POST',
        label: 'start new LiDAR session',
      });
      await refreshSessions();
    } catch (error) {
      console.error('Error starting new session:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative w-full h-screen">
      {/* Debug Panel */}
      <div className="absolute top-0 left-0 right-0 bg-black/80 text-white p-4 z-20">
        <div className="container mx-auto">
          <button
            onClick={() => setIsDebugExpanded(!isDebugExpanded)}
            className="flex items-center justify-between w-full text-lg font-bold mb-2"
          >
            <span>Debug Information</span>
            <span>{isDebugExpanded ? '▼' : '▶'}</span>
          </button>
          {isDebugExpanded && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold">Point 1:</h4>
                <pre className="text-sm">
                  {debugInfo.point1
                    ? `Position: (${debugInfo.point1.position.x.toFixed(2)}, ${debugInfo.point1.position.y.toFixed(2)}, ${debugInfo.point1.position.z.toFixed(2)})
Original: (${debugInfo.point1.originalCoords.x.toFixed(2)}, ${debugInfo.point1.originalCoords.y.toFixed(2)}, ${debugInfo.point1.originalCoords.z.toFixed(2)})`
                    : 'Not selected'}
                </pre>
              </div>
              <div>
                <h4 className="font-semibold">Point 2:</h4>
                <pre className="text-sm">
                  {debugInfo.point2
                    ? `Position: (${debugInfo.point2.position.x.toFixed(2)}, ${debugInfo.point2.position.y.toFixed(2)}, ${debugInfo.point2.position.z.toFixed(2)})
Original: (${debugInfo.point2.originalCoords.x.toFixed(2)}, ${debugInfo.point2.originalCoords.y.toFixed(2)}, ${debugInfo.point2.originalCoords.z.toFixed(2)})`
                    : 'Not selected'}
                </pre>
              </div>
              <div className="col-span-2">
                <h4 className="font-semibold">Measurement:</h4>
                <pre className="text-sm">
                  {debugInfo.distance ? `Distance: ${debugInfo.distance} cm` : 'No measurement yet'}
                </pre>
              </div>
              <div className="col-span-2">
                <h4 className="font-semibold">Mode:</h4>
                <pre className="text-sm">
                  Measurement Mode: {measureMode ? 'Active' : 'Inactive'}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Controls Panel */}
      <div className="absolute top-32 left-4 z-10 bg-white/90 p-4 rounded-lg shadow-lg">
        {/*<Button onClick={startNewSession} disabled={loading}>*/}
        {/*  🚀 Start New Session*/}
        {/*</Button>*/}
        {loading && (
          <div className="mt-4">
            <Progress value={loading ? 50 : 0} />
            <p className="text-sm mt-2">Processing...</p>
          </div>
        )}
        {/*<div className="mt-4">*/}
        {/*  <label className="block text-sm font-medium mb-2">Select Session:</label>*/}
        {/*  <select*/}
        {/*    value={selectedSession}*/}
        {/*    onChange={e => {*/}
        {/*      setSelectedSession(e.target.value);*/}
        {/*      loadSession(e.target.value);*/}
        {/*    }}*/}
        {/*    className="w-full p-2 border rounded"*/}
        {/*  >*/}
        {/*    {sessions.map(session => (*/}
        {/*      <option key={session} value={session}>*/}
        {/*        {session}*/}
        {/*      </option>*/}
        {/*    ))}*/}
        {/*  </select>*/}
        {/*</div>*/}
        <div className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Upload 360° Image:</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full p-2 border rounded"
            />
            {uploadedFiles.image && <p className="text-sm text-green-600 mt-1">✓ Image uploaded</p>}
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Upload Points CSV:</label>
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="w-full p-2 border rounded"
            />
            {uploadedFiles.csv && <p className="text-sm text-green-600 mt-1">✓ CSV uploaded</p>}
          </div>
          <div className="space-y-2">
            <Button
              onClick={() => {
                setMeasureMode(!measureMode);
                setDebugInfo(prev => ({
                  ...prev,
                  measurementMode: !measureMode,
                  point1: null,
                  point2: null,
                  distance: null,
                }));
                if (measureMode) {
                  clearMeasurement();
                }
              }}
              className={`${measureMode ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
            >
              🧭 {measureMode ? 'Measuring (click to disable)' : 'Toggle Measure Mode'}
            </Button>
            <Button
              onClick={() => {
                document
                  .getElementById('cameraRig')
                  .setAttribute('position', { x: 0, y: 1.6, z: 4 });
                setRotation(0);
                document.getElementById('pointCloud').setAttribute('rotation', '0 0 0');
              }}
            >
              🔄 Reset View
            </Button>
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Rotate Point Cloud Y-axis:</label>
              <input
                type="range"
                min="0"
                max="360"
                value={rotation}
                onChange={e => {
                  const angle = e.target.value;
                  setRotation(angle);
                  document.getElementById('pointCloud').setAttribute('rotation', `0 ${angle} 0`);
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Measurements Panel */}
      <div className="absolute top-32 right-4 z-10 bg-white/90 p-4 rounded-lg shadow-lg w-64">
        <div className="flex justify-between items-center mb-4">
          <strong>Measurements:</strong>
          <Button onClick={() => setMeasurements([])}>🧹 Clear</Button>
        </div>
        <div className="space-y-2 max-h-[calc(100vh-400px)] overflow-y-auto">
          {measurements.map((measurement, index) => (
            <div key={index} className="border-b pb-2">
              📏 {measurement.distance} cm
            </div>
          ))}
        </div>
      </div>

      <a-scene>
        <a-entity id="cursor" cursor="rayOrigin: mouse" raycaster="objects: .clickable"></a-entity>
        <a-sky
          id="sky"
          color="#ffffff"
          type="equirectangular"
          radius="5000"
          crossorigin="anonymous"
        ></a-sky>
        <a-entity id="pointCloud" position="0 0 0"></a-entity>
        <a-entity id="measureLine" visible="false" line="color: #ff0000"></a-entity>
        <a-entity id="measureLabel" text="value: " position="0 2 -2"></a-entity>
        <a-entity id="cameraRig" position="0 1.6 4">
          <a-entity camera look-controls wasd-controls position="0 0 0"></a-entity>
        </a-entity>
        <a-entity light="type: ambient; color: #ffffff; intensity: 1"></a-entity>
        <a-entity
          light="type: directional; color: #ffffff; intensity: 0.5"
          position="0 1 1"
        ></a-entity>
      </a-scene>

      {!imageLoaded && loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-50">
          <div className="text-white text-center">
            <Progress value={loading ? 50 : 0} />
            <p className="mt-2">Loading 360° image and point cloud...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default LidarViewer;
